import React from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { IS_FORD_BRAND } from "../../../utils/EmpUtil";
import { DEALER_ONBOARDING_PROGRESS_QUERY_KEY } from "../../../shared/utils/onboardingProgressUtil";
import DealerOnboardingClient from "../../../clients/DealerOnboardingClient";
import OnboardingPageTitle from "../../../shared/components/OnboardingPageTitle/OnboardingPageTitle";
import fordLoader from "../../../imgs/ford-loader.svg";
import lincolnLoader from "../../../imgs/lincoln-loader.svg";

const proximaRegularFontFamily =
  '"ProximaNovaRegular", "Lincoln Proxima Nova", Arial, sans-serif';

const bodyStyle = {
  color: "var(--color-text-onlight-strong, #000)",
  fontFamily: proximaRegularFontFamily,
  fontSize: "var(--body-1-regular-font-size, 16px)",
  fontStyle: "normal",
  fontWeight: "var(--body-1-regular-font-weight, 400)",
  lineHeight: "var(--body-1-regular-line-height, 24px)",
};

const GetStartedContent = ({ user, brandName, progressRequest }) => {
  const { t } = useTranslation();
  const isFordBrand = IS_FORD_BRAND(user?.brand);

  const { isLoading: progressLoading } = useQuery({
    queryKey: [
      DEALER_ONBOARDING_PROGRESS_QUERY_KEY,
      progressRequest?.dealerCommonId,
    ],
    queryFn: () =>
      DealerOnboardingClient.getDealerOnboardingProgress(
        progressRequest.dealerCommonId,
        progressRequest.dealerInfo
      ),
    enabled: !!progressRequest,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  return (
    <Box className="tw-bg-white tw-rounded-lg tw-px-8 tw-pb-8">
      <OnboardingPageTitle testId="get-started-title" className="tw-mb-6">
        {t("DealerOnboarding.content.getStarted.title", "Get started")}
      </OnboardingPageTitle>

      <Typography
        variant="body1"
        component="p"
        className="tw-mb-6"
        style={bodyStyle}
      >
        {t("DealerOnboarding.content.getStarted.welcomeText", {
          currentBrand: brandName,
        })}
      </Typography>

      <Typography variant="body1" component="p" style={bodyStyle}>
        {t("DealerOnboarding.content.getStarted.timeEstimate")}
      </Typography>

      {progressLoading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "200px",
          }}
          data-testid="progress-loading-spinner"
        >
          <img
            src={isFordBrand ? fordLoader : lincolnLoader}
            alt="Loading"
            style={{ width: "130px", height: "50px" }}
          />
        </Box>
      )}
    </Box>
  );
};

GetStartedContent.propTypes = {
  user: PropTypes.object,
  brandName: PropTypes.string.isRequired,
  progressRequest: PropTypes.object,
};

export default GetStartedContent;
